/**
 * Pure helpers for the sender-email "warming" lifecycle gating in the
 * sequence-copy flow. Kept dependency-free so they can be unit-tested in the
 * repo's node-env vitest (no jsdom / component rendering).
 */

export interface WarmingPartition {
  /** Ids safe to attach (not currently warming). */
  attachableIds: number[]
  /** How many candidates were dropped because they are still warming. */
  skippedCount: number
}

/**
 * Split candidate sender-email ids into those that can be attached to a
 * campaign and a count of those skipped because they are still warming.
 *
 * Warming senders are rejected by the attach endpoint, and it rejects the
 * whole batch if any member is warming, so the sequence copy filters them out
 * client-side before attaching (see sequence-wizard-page-v2).
 */
export function partitionWarmingSenders(
  candidateIds: number[],
  warmingSenderIds: Set<number>
): WarmingPartition {
  const attachableIds = candidateIds.filter((id) => !warmingSenderIds.has(id))
  return {
    attachableIds,
    skippedCount: candidateIds.length - attachableIds.length,
  }
}

/**
 * User-facing message when warming senders were left out of a sequence copy.
 * Returns null when nothing was skipped (so the caller shows no toast).
 */
export function warmingSkippedMessage(skippedCount: number): string | null {
  if (skippedCount <= 0) return null
  const subject =
    skippedCount === 1
      ? '1 sender email is'
      : `${skippedCount} sender emails are`
  const verb = skippedCount === 1 ? 'was' : 'were'
  return `${subject} still warming up and ${verb} not copied to this campaign.`
}
